// The practice surface's three `fetch` calls, in the `rounds-session.ts` / `rounds-http.ts` mould
// (`rounds-practice-state`, `rounds-practice-admission`, `rounds-run-summary`).
//
// **There are exactly three, and that is the contract, not an accident.**
//
//   1. `POST /rounds/practice/session` — open the device's practice sandbox. Answers one of the
//      three typed outcomes (`created` / `resumed` / `expired`), never a silently empty sandbox.
//   2. `POST /rounds/{scope}/runs/local` — the **single** run-start request. It commits the working
//      document, allocates the run, and returns the **run token** bound to the version it committed,
//      the configuration and the server's sequence. No second protocol is introduced
//      (`client-sandbox-screen`).
//   3. `POST /rounds/{scope}/runs/{runId}/summary` — the one bounded, idempotent terminal
//      submission, under that token.
//
// Everything else a practice sandbox needs — the colony read, the document write, version history,
// the Beekeeper thread — is the **shipped** `RoundsHttpClient` against the practice scope, because a
// practice sandbox is a sandbox. Nothing new is needed there and nothing is duplicated here.
//
// **Typed refusals are values, never exceptions**, the convention the whole rounds client already
// follows: a silent no-op on an entry path is the one failure mode that must not happen.
//
// > **Server-side status.** The routes above are `tasks.md` groups 6 and 7's to build. This module is
// > the client half of the contract and the shapes the server must match; it is deliberately written
// > once, here, rather than inline in the composition root, so that agreement is reviewable in one
// > place.

import type { RunRefusal } from '../ui/colony/run-controller';
import type { PracticeRunSummary } from '../app/rounds/practice-runner';

/** Where the practice routes live. Named so the paths appear exactly once. */
export const PRACTICE_SESSION_PATH = '/rounds/practice/session';

export function practiceLocalRunPath(scope: string): string {
  return `/rounds/${encodeURIComponent(scope)}/runs/local`;
}

export function practiceRunSummaryPath(scope: string, runId: number): string {
  return `/rounds/${encodeURIComponent(scope)}/runs/${runId}/summary`;
}

// ---------------------------------------------------------------------------------------
// 1. Opening the sandbox
// ---------------------------------------------------------------------------------------

/** What every open answers with, whichever outcome it is. */
export interface PracticeSessionHandle {
  /** The bearer token the practice scope's other routes authorize with. */
  token: string;
  /** The durable scope identifier, carrying the explicit `practice` domain tag
   * (`rounds-practice-state`) — never a round number and never a synthetic round. */
  scope: string;
  /** The configured idle period, in days, as the server reports it. Server configuration, not a
   * balance value (**D-F**), so the client states what it is told rather than a constant of its own. */
  retentionDays: number;
}

/**
 * The three typed outcomes (`rounds-practice-state` "Opening a practice sandbox returns a typed
 * outcome"), plus the two the transport itself can produce.
 *
 * `expired` is the load-bearing one: a credential whose work was discarded is **never** told it is
 * new, because the server resolves against the expiry tombstone before concluding `created`.
 */
export type PracticeOpenOutcome =
  | { kind: 'created'; session: PracticeSessionHandle }
  | {
      kind: 'resumed';
      session: PracticeSessionHandle;
      /** When the sandbox was last *used* — the persisted last-activity instant, epoch ms. */
      lastActivityAt: number;
    }
  | {
      kind: 'expired';
      /** Read from the tombstone, epoch ms. */
      expiredAt: number;
      retentionDays: number;
    }
  /**
   * **The credential names no sandbox here, and none was created in finding that out** — the
   * resume-only answer (`client-practice` "A practice session is restored on its own route and mints
   * nothing to do so").
   *
   * Distinct from `expired`, and the distinction is worth a variant: `expired` means "your work was
   * kept for a while and has now gone", which a player deserves to be told, whereas this means the
   * stored credential simply is not a sandbox on this server — a purge, a reset, or a credential from
   * another deployment. Nothing to say and nothing to mourn.
   */
  | { kind: 'absent' }
  /** `404` — this server mounts no rounds surface, or no practice surface on it. */
  | { kind: 'unsupported' }
  /** Any other named refusal, or a request that never got an answer. */
  | { kind: 'unavailable'; message: string };

export interface OpenPracticeSessionConfig {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** The device-minted practice credential (**D17**). */
  credential: string;
  /** `true` to provision a fresh sandbox, clearing any expiry tombstone in the same transaction
   * (`rounds-practice-state` "Starting over clears the tombstone in the same transaction"). */
  startOver?: boolean;
  /**
   * **`true` to resume only, never provisioning.**
   *
   * The *restore* path's flag — a reload of `/practice` presenting whatever credential the device had
   * stored — as against the *open* path, which is the player's explicit act on Home and may mint. A
   * credential the server does not recognise then answers `absent` instead of being re-provisioned
   * into a fresh empty colony.
   *
   * A server flag rather than client-side treatment of a `created` outcome (**Q4**), because the
   * client-side version leaves the sandbox *provisioned* and merely unadopted: it consumes the
   * active-identity cap `rounds-practice-admission` enforces, so a player whose restores kept minting
   * would fill their allowance with colonies nobody ever sees.
   */
  resumeOnly?: boolean;
}

const DEFAULT_RETENTION_DAYS = 30;

function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * A non-empty string, or `undefined` — for fields where **absence is a meaning of its own** and no
 * default exists to fall back to.
 *
 * Deliberately not a coercion: `String(value)` would turn a number into `"42"` and an object into
 * `"[object Object]"`, both of which look like data the caller may act on. A server that answered
 * with the wrong *type* on such a field has said nothing this client can use, and saying nothing is
 * what it is reported as.
 */
function stringOrAbsent(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function handleOf(body: Record<string, unknown>): PracticeSessionHandle | null {
  const token = body.token;
  const scope = body.sandbox ?? body.scope;
  if (typeof token !== 'string' || typeof scope !== 'string' || token === '' || scope === '') {
    return null;
  }
  return {
    token,
    scope,
    retentionDays: numberOr(body.retentionDays, DEFAULT_RETENTION_DAYS),
  };
}

export async function openPracticeSession(
  config: OpenPracticeSessionConfig,
): Promise<PracticeOpenOutcome> {
  const url = `${config.baseUrl ?? ''}${PRACTICE_SESSION_PATH}`;
  const send = config.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await send(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credential: config.credential,
        ...(config.startOver === true ? { startOver: true } : {}),
        ...(config.resumeOnly === true ? { resumeOnly: true } : {}),
      }),
    });
  } catch (error) {
    return { kind: 'unavailable', message: messageOf(error) };
  }
  if (response.status === 404) {
    return { kind: 'unsupported' };
  }
  let body: Record<string, unknown>;
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    return { kind: 'unavailable', message: 'The hive answered with something unreadable.' };
  }
  if (!response.ok) {
    return {
      kind: 'unavailable',
      message: typeof body.error === 'string' ? body.error : `practice open failed (${response.status})`,
    };
  }

  // The outcome is the server's own word, never inferred from whether a document came back — which
  // is exactly how a discarded sandbox would end up presented as an empty new one.
  const outcome = body.outcome;
  if (outcome === 'absent') {
    // Resume-only found no sandbox for this credential. No handle is expected and none is read: a
    // response that carried one would be describing a session this request declined to create.
    return { kind: 'absent' };
  }
  if (outcome === 'expired') {
    return {
      kind: 'expired',
      expiredAt: numberOr(body.expiredAt, 0),
      retentionDays: numberOr(body.retentionDays, DEFAULT_RETENTION_DAYS),
    };
  }
  const session = handleOf(body);
  if (session === null) {
    return { kind: 'unavailable', message: 'The hive did not name a practice sandbox.' };
  }
  if (outcome === 'resumed') {
    return { kind: 'resumed', session, lastActivityAt: numberOr(body.lastActivityAt, 0) };
  }
  if (outcome === 'created') {
    return { kind: 'created', session };
  }
  return { kind: 'unavailable', message: 'The hive named an outcome this client does not know.' };
}

// ---------------------------------------------------------------------------------------
// 2. The single run-start request
// ---------------------------------------------------------------------------------------

/**
 * The run-start request — the document and the ordinal it was edited from, and **nothing else**.
 *
 * `rivals` and `seed` used to be here. Both are gone rather than defaulted (`home-and-single-player`):
 * the rival count is fixed at three, and the seed is *server configuration* so that every score in the
 * game is comparable. A client that still sent either — even a constant — would be a second source of
 * truth for a value the operator sets, and would make it possible for one player's score to mean
 * something different from another's.
 */
export interface LocalRunStartRequest {
  /** The working document's bytes, verbatim (**D8**) — never a per-type reassembly. */
  document: string;
  /** The ordinal the document was edited from, for the server's compare-and-swap. */
  baseOrdinal: number;
}

/**
 * The accept response. It carries the **run token** bound to the version *this run's own commit*
 * cut — which is what makes it impossible for a token to name a document ordinal other than the one
 * actually simulated, and why the version the selector shows is never a later head a concurrent
 * writer produced.
 */
export type LocalRunStartOutcome =
  | {
      kind: 'accepted';
      runId: number;
      documentVersion: number;
      token: string;
      /** The run's horizon, from the server's own `total_ticks` — so the device's progress is
       * determinate against a number it did not invent. */
      terminalTick: number;
      /**
       * **The seed this run simulates, from server configuration** (`home-and-single-player`).
       *
       * It arrives here rather than being minted on the device because the sandbox is a ranked ladder:
       * every run has to be on the same field for a better score to mean a better script. It is
       * therefore the operator's to rotate — a new seed starts a second ladder rather than corrupting
       * the first — and the client has no control over it and no reroll.
       *
       * Empty when the server did not name one, which the device must refuse rather than substitute a
       * seed for: a run on an unknown field is exactly the incomparable score the fixed seed exists to
       * prevent.
       */
      seed: string;
      /**
       * **The world this run must simulate**, as the server's own recorded TOML — the resolved
       * `Balance` for *this* run's roster, in the bytes the trace's balance component is written
       * from.
       *
       * It is delivered rather than named because the device cannot look a preset up: the engine
       * reaches exactly one, the `seed` preset compiled into the WebAssembly module, and a server
       * running any other one (`rounds`, whose field size and patch count are derived from the
       * roster) would otherwise have its practice runs silently simulate a different world than the
       * graded round, with nothing reporting an error.
       *
       * **`undefined` is meaningful and is not a default.** It means "the preset embedded in the
       * engine", which is what the engine resolves to when handed no balance. It is never
       * substituted for a balance the server *did* send: a malformed one is passed through, because
       * the engine refuses an unreadable recorded balance as a typed refusal and dropping it here
       * would downgrade the run to the embedded world instead.
       */
      balanceToml?: string;
      /**
       * The name that balance was selected under, carried for **provenance only**: the device writes
       * its own trace manifest, which records the preset's name beside the balance it ran, and a
       * device that invented the name would write `"seed"` over some other geometry.
       *
       * Server configuration, never a request input (DESIGN §14) — this client neither sends it nor
       * chooses it, and the same name is already on every trace manifest the server serves
       * (`rounds-trace.ts`'s `balancePreset`).
       *
       * `undefined` alongside an `undefined` {@link balanceToml} is the coherent pair: the run uses
       * the embedded preset. `undefined` *beside* a present balance is a server that half-answered,
       * and the caller must not fill it in — see `PracticeRunPlan`.
       */
      balancePreset?: string;
    }
  | { kind: 'refused'; refusal: RunRefusal };

export interface LocalRunTransportConfig {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  scope: string;
  token: string;
}

export interface PracticeRunTransport {
  startLocalRun(request: LocalRunStartRequest): Promise<LocalRunStartOutcome>;
  submitRunSummary(
    runId: number,
    token: string,
    summary: PracticeRunSummary,
  ): Promise<PracticeSummaryOutcome>;
}

/** The terminal submission's outcome. Idempotent server-side; a retry is `recorded` again. */
export type PracticeSummaryOutcome =
  | { kind: 'recorded' }
  /** `401`/`403` — the token is unknown, expired, or names another scope. */
  | { kind: 'rejected'; reason: string }
  | { kind: 'unavailable'; message: string };

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'The hive could not be reached.';
}

/** The production transport over the two run routes. */
export function createPracticeRunTransport(config: LocalRunTransportConfig): PracticeRunTransport {
  const send = config.fetchImpl ?? fetch;
  const auth = { Authorization: `Bearer ${config.token}`, 'Content-Type': 'application/json' };
  const base = config.baseUrl ?? '';

  return {
    async startLocalRun(request: LocalRunStartRequest): Promise<LocalRunStartOutcome> {
      let response: Response;
      try {
        response = await send(`${base}${practiceLocalRunPath(config.scope)}`, {
          method: 'POST',
          headers: auth,
          body: JSON.stringify(request),
        });
      } catch (error) {
        // An unreachable server means **no run starts** — never a run whose summary could not be
        // attributed (`client-sandbox-screen`).
        return { kind: 'refused', refusal: { kind: 'unavailable', message: messageOf(error) } };
      }
      let body: Record<string, unknown>;
      try {
        body = (await response.json()) as Record<string, unknown>;
      } catch {
        return {
          kind: 'refused',
          refusal: { kind: 'unavailable', message: 'The hive answered with something unreadable.' },
        };
      }
      if (!response.ok) {
        return { kind: 'refused', refusal: refusalOf(response.status, body) };
      }
      const runId = body.runId;
      const token = body.token;
      if (typeof runId !== 'number' || typeof token !== 'string' || token === '') {
        // **No run starts without its token** (`rounds-local-run`). A run with no token could
        // never report its summary, so admitting one would orphan it by construction.
        return {
          kind: 'refused',
          refusal: { kind: 'unavailable', message: 'The hive accepted the run without a token.' },
        };
      }
      return {
        kind: 'accepted',
        runId,
        documentVersion: numberOr(body.documentVersion, 0),
        token,
        terminalTick: numberOr(body.terminalTick, 0),
        // **No fallback**, for the same reason `balanceToml` has none: a substituted seed is a
        // complete, plausible field the run would simulate without complaint, and its score would be
        // incomparable with every other score on the ladder. Absent stays absent, and the engine
        // refuses it.
        seed: typeof body.seed === 'string' ? body.seed : '',
        // **No fallback**, unlike every number above. `numberOr`'s default is safe because a
        // horizon of 0 is visibly wrong; a *balance* default would be a complete, plausible world
        // the run would simulate without complaint. Absent stays absent.
        balanceToml: stringOrAbsent(body.balanceToml),
        balancePreset: stringOrAbsent(body.balancePreset),
      };
    },

    async submitRunSummary(runId, token, summary): Promise<PracticeSummaryOutcome> {
      let response: Response;
      try {
        response = await send(`${base}${practiceRunSummaryPath(config.scope, runId)}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(summary),
        });
      } catch (error) {
        return { kind: 'unavailable', message: messageOf(error) };
      }
      if (response.ok) {
        return { kind: 'recorded' };
      }
      let reason = `summary refused (${response.status})`;
      try {
        const body = (await response.json()) as Record<string, unknown>;
        if (typeof body.error === 'string') {
          reason = body.error;
        }
      } catch {
        // Keep the status-shaped reason.
      }
      return { kind: 'rejected', reason };
    },
  };
}

/** Maps a refused run-start onto the shipped `RunRefusal` set, so the sandbox screen's existing
 * refusal copy covers a local run without a second vocabulary. */
function refusalOf(status: number, body: Record<string, unknown>): RunRefusal {
  const error = typeof body.error === 'string' ? body.error : `run refused (${status})`;
  // Discriminated on the body before the status, because 409 is no longer one condition. The
  // turn-exclusivity gate the practice fork gained on 2026-08-07 refuses a Run held behind a
  // Beekeeper turn on this same route with the same status, and the two need opposite things from
  // the player: staleness says the document moved and must be re-opened, a held turn says nothing
  // is wrong and the press will work shortly. A bare 409 with no typed body keeps its original
  // meaning below.
  if (error === 'turn_in_progress') {
    return { kind: 'turnInProgress' };
  }
  if (status === 409 || error === 'stale') {
    return { kind: 'stale' };
  }
  if (status === 403 || error === 'unauthorized') {
    return { kind: 'unauthorized' };
  }
  if (error === 'compile_failed') {
    const diagnostics = Array.isArray(body.diagnostics) ? body.diagnostics : [];
    return {
      kind: 'compileFailed',
      diagnostics: diagnostics.flatMap((entry) => {
        const row = entry as Record<string, unknown>;
        return typeof row.message === 'string'
          ? [{ typeName: typeof row.typeName === 'string' ? row.typeName : '', message: row.message }]
          : [];
      }),
    };
  }
  return { kind: 'invalid', error };
}
